import React, { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Button from '../../../components/ui/Button';

const PaymentForm = ({ clientSecret, onSuccess, onError, processing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: window?.location?.origin + '/subscription-checkout-payment-portal'
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error?.message);
        onError?.(error);
      } else if (paymentIntent && paymentIntent?.status === 'succeeded') {
        await onSuccess?.(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage(err?.message || 'An unexpected error occurred');
      onError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Payment Details
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Enter your payment information to complete your subscription
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <PaymentElement />
        </div>

        {errorMessage && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800 dark:text-red-200">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="mb-6 bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                Secure Payment
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Your payment information is encrypted and secure. We use Stripe for payment processing and never store your card details.
              </p>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          fullWidth
          loading={isProcessing || processing}
          disabled={!stripe || !elements || isProcessing || processing}
        >
          {isProcessing || processing ? 'Processing Payment...' : 'Complete Payment & Activate Account'}
        </Button>

        <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
          By completing this payment, you agree to our Terms of Service and Privacy Policy
        </p>
      </form>
    </div>
  );
};

export default PaymentForm;